import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Chip,
  Divider,
  alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const ComprehensionActivity = ({ activity }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const correctColor = isLight ? theme.palette.primary.main : theme.palette.success.main;
  const correctBgColor = isLight ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.success.main, 0.1);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper
      }}
    >
      <Typography variant="h6" gutterBottom color="text.primary">
        {activity.activityTitle}
      </Typography>

      <Box sx={{ mb: 1 }}>
        <Chip
          label={`${activity.content.questions.length} Questions`}
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            fontWeight: 500
          }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {activity.content.questions.map((question, qIndex) => (
        <Accordion
          key={question.id}
          defaultExpanded={qIndex === 0}
          disableGutters
          elevation={0}
          sx={{
            bgcolor: theme.palette.background.default,
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha(theme.palette.common.white, 0.1),
            '&:before': {
              display: 'none'
            },
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon
                sx={{ color: theme.palette.text.secondary, fontSize: 24 }}
              />
            }
            aria-controls={`question-${qIndex}-content`}
            id={`question-${qIndex}-header`}
            sx={{
              bgcolor: alpha(theme.palette.common.white, 0.03),
              '&.Mui-expanded': {
                minHeight: '56px',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                label={qIndex + 1}
                size="small"
                sx={{
                  mr: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  height: 24,
                  width: 24,
                  borderRadius: '50%'
                }}
              />
              <Typography fontWeight={500} color="text.primary">
                {question.text}
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 3 }}>
            <RadioGroup>
              {question.options.map((option, oIndex) => (
                <FormControlLabel
                  key={oIndex}
                  value={String.fromCharCode(65 + oIndex)}
                  control={
                    <Radio
                      checked={question.correctIndex === oIndex}
                      sx={{
                        color: question.correctIndex === oIndex
                          ? correctColor
                          : theme.palette.text.secondary
                      }}
                    />
                  }
                  sx={{
                    width: '100%',
                    m: 0,
                    p: 1.5,
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: question.correctIndex === oIndex
                      ? correctBgColor
                      : alpha(theme.palette.common.white, 0.02),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.common.white, 0.05)
                    }
                  }}
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        sx={{ fontWeight: question.correctIndex === oIndex ? 600 : 400 }}
                      >
                        {option}
                      </Typography>

                      {question.correctIndex === oIndex && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mt: 1,
                          color: correctColor
                        }}>
                          <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="caption" fontWeight={500}>
                            Correct answer
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
};

export default ComprehensionActivity;